import { isRecord } from './http.js';
import { isMatchOutcome, isOnlineGame, ONLINE_GAMES } from './types.js';
import type { GameStats, HistoryStats, MatchRecord } from './types.js';

/**
 * Narrowing for every response body. The Worker and this package are versioned
 * together, but a stale cached bundle or an intermediary error page can still
 * deliver something else — so every field is checked and a mismatch becomes an
 * `unknown` GameHistoryError rather than a runtime crash deep in a UI render.
 */

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

/**
 * `players` / `seed` / `moves` pass through unchecked — they're opaque, this
 * package cannot know their shape, only that the field exists in the raw
 * object. The Worker itself validated them as objects/arrays before ever
 * accepting the write, so re-checking here would just risk drifting from
 * what the server actually accepts.
 */
export function toMatchRecord(raw: unknown): MatchRecord | null {
  if (!isRecord(raw)) return null;
  const id = str(raw['id']);
  const variant = str(raw['variant']);
  const createdAt = num(raw['createdAt']);
  const finishedAt = num(raw['finishedAt']);
  const outcome = raw['outcome'];
  const game = raw['game'];
  if (id === null || variant === null || createdAt === null || finishedAt === null) return null;
  if (!isMatchOutcome(outcome) || !isOnlineGame(game)) return null;
  return {
    id,
    game,
    variant,
    outcome,
    players: raw['players'],
    seed: raw['seed'],
    moves: raw['moves'],
    createdAt,
    finishedAt,
  };
}

export function toGameStats(raw: unknown): GameStats | null {
  if (!isRecord(raw)) return null;
  const wins = num(raw['wins']);
  const losses = num(raw['losses']);
  const draws = num(raw['draws']);
  const abandoned = num(raw['abandoned']);
  const gamesPlayed = num(raw['gamesPlayed']);
  if (wins === null || losses === null || draws === null) return null;
  if (abandoned === null || gamesPlayed === null) return null;
  return { wins, losses, draws, abandoned, gamesPlayed };
}

/** Fails closed: a missing or invalid `byGame` key rejects the whole payload
 * rather than silently defaulting, matching `decode.ts`'s stated philosophy. */
export function toHistoryStats(raw: unknown): HistoryStats | null {
  if (!isRecord(raw)) return null;
  const overall = toGameStats(raw['overall']);
  if (overall === null) return null;

  const byGameRaw = raw['byGame'];
  if (!isRecord(byGameRaw)) return null;

  const byGame: Partial<Record<(typeof ONLINE_GAMES)[number], GameStats>> = {};
  for (const game of ONLINE_GAMES) {
    const stats = toGameStats(byGameRaw[game]);
    if (stats === null) return null;
    byGame[game] = stats;
  }

  return { byGame: byGame as Record<(typeof ONLINE_GAMES)[number], GameStats>, overall };
}

/** Unwraps `{ <key>: [...] }` envelopes, rejecting the whole list on one bad item. */
export function toList<T>(
  raw: unknown,
  key: string,
  item: (entry: unknown) => T | null
): T[] | null {
  if (!isRecord(raw)) return null;
  const entries = raw[key];
  if (!Array.isArray(entries)) return null;
  const decoded: T[] = [];
  for (const entry of entries) {
    const value = item(entry);
    if (value === null) return null;
    decoded.push(value);
  }
  return decoded;
}

/** Unwraps a `{ <key>: {...} }` envelope. */
export function toWrapped<T>(
  raw: unknown,
  key: string,
  item: (entry: unknown) => T | null
): T | null {
  if (!isRecord(raw)) return null;
  return item(raw[key]);
}
